import React, { useEffect, useState, useMemo } from "react";
import instance from "../instance";
import { useNavigate, useLocation } from "react-router-dom";
import CustomerSearch from "./Customers/CustomerSearch";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-timezone";

const PriceProductsCity = () => {
  const formatDateToCustomString = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const hasMore = true;

  const [searchTerm, setSearchTerm] = useState("");
  const [excludeTerm, setExcludeTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "Date",
    direction: "descending",
  });

  const [userRole, setUserRole] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const fetchData = async (
    date = moment().tz("Africa/Cairo").format("YYYY-MM-DD")
  ) => {
    try {
      const response = await instance.get(`products/price-city?date=${date}`);
      if (page === 1) {
        setData(response.data.data);
      } else {
        setData((prevData) => [...prevData, ...response.data.data]);
      }
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData is recreated each render; adding it would refetch on every paint
  }, []);

  useEffect(() => {
    const role = localStorage.getItem("role");
    if (role) {
      setUserRole(role);
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const nameFilter = queryParams.get("name");
    if (nameFilter) {
      setSearchTerm(nameFilter);
    }
  }, [location.search]);

  const handleSort = (column) => {
    const direction =
      sortConfig.direction === "ascending" ? "descending" : "ascending";
    setSortConfig({ key: column, direction });
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleExcludeSearch = (event) => {
    setExcludeTerm(event.target.value);
  };

  const resetSearch = () => {
    setSearchTerm("");
    setExcludeTerm("");
  };

  const getNextDate = (page) => {
    const currentDate = moment().tz("Africa/Cairo");

    currentDate.subtract(page - 1, "days");
    return currentDate.format("YYYY-MM-DD");
  };

  const loadMore = () => {
    const nextDate = getNextDate(page);
    setPage((prevPage) => prevPage + 1);
    fetchData(nextDate);
  };

  const getMinMaxPrices = (data) => {
    const pricesByDateAndProduct = {};
    data.forEach((item) => {
      const key = `${item.Date}_${item.Product}`;
      if (!pricesByDateAndProduct[key]) {
        pricesByDateAndProduct[key] = {
          min: Infinity,
          max: -Infinity,
        };
      }
      pricesByDateAndProduct[key].min = Math.min(
        pricesByDateAndProduct[key].min,
        parseFloat(item.Min_Price)
      );
      pricesByDateAndProduct[key].max = Math.max(
        pricesByDateAndProduct[key].max,
        parseFloat(item.Max_Price)
      );
    });
    return pricesByDateAndProduct;
  };

  const getPriceBackgroundColor = (
    price,
    minMaxPrices,
    date,
    product,
    isMax
  ) => {
    const key = `${date}_${product}`;
    if (minMaxPrices[key]) {
      if (isMax && parseFloat(price) === minMaxPrices[key].max) {
        return "rgba(255, 0, 0, 0.2)"; // Light red background for max price
      }
      if (!isMax && parseFloat(price) === minMaxPrices[key].min) {
        return "rgba(0, 255, 0, 0.2)"; // Light green background for min price
      }
    }
    return "transparent";
  };

  const sortedData = useMemo(() => {
    let sorted = [...data];

    sorted = sorted.filter((item) => {
      const searchTermLower = searchTerm.toLowerCase();
      const excludeTermLower = excludeTerm.toLowerCase();

      const itemValues = [
        item.Product,
        item.Max_Price,
        item.Min_Price,
        formatDateToCustomString(item.Date),
        item.City,
      ].map((value) => (value ? value.toString().toLowerCase() : ""));

      const matchesSearchTerm =
        searchTerm === "" ||
        itemValues.some((value) => value.includes(searchTermLower));
      const matchesExcludeTerm =
        excludeTerm === "" ||
        !itemValues.some((value) => value.includes(excludeTermLower));

      return matchesSearchTerm && matchesExcludeTerm;
    });

    sorted.sort((a, b) => {
      const aValue =
        sortConfig.key === "Date"
          ? new Date(a[sortConfig.key])
          : a[sortConfig.key];
      const bValue =
        sortConfig.key === "Date"
          ? new Date(b[sortConfig.key])
          : b[sortConfig.key];

      const isNumeric = ["Max_Price", "Min_Price"].includes(sortConfig.key);
      const aNumber = isNumeric ? parseFloat(aValue) : aValue;
      const bNumber = isNumeric ? parseFloat(bValue) : bValue;

      if (aNumber < bNumber) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aNumber > bNumber) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });

    const minMaxPrices = getMinMaxPrices(sorted);
    return sorted.map((item, index) => ({
      ...item,
      sequence: index + 1,
      minMaxPrices: minMaxPrices,
    }));
  }, [data, sortConfig, searchTerm, excludeTerm]);

  const getAllUniqueValues = () => {
    const uniqueValues = new Set();
    data.forEach((item) => {
      Object.values(item).forEach((value) => {
        if (value instanceof Date || !isNaN(Date.parse(value))) {
          uniqueValues.add(formatDateToCustomString(value));
        } else {
          uniqueValues.add(value);
        }
      });
    });
    return Array.from(uniqueValues).sort((a, b) => (a > b ? -1 : 1));
  };

  const getDateCellColor = (dateString) => {
    if (!dateString) return "transparent";

    const now = new Date();
    const currentDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    const date = new Date(dateString);
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    const past6Days = new Date(currentDate.getTime() - 6 * 24 * 60 * 60 * 1000);

    if (dateOnly.getTime() === currentDate.getTime()) {
      return "rgba(8, 171, 14, 0.3)";
    } else if (dateOnly < past6Days) {
      return "rgba(255, 0, 0, 0.2)";
    } else {
      return "transparent";
    }
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  const handleRunTaskSequence = async () => {
    try {
        // تنفيذ المهمة الأولى
        await instance.post("price/run-price2");
        toast.success("Task 1 executed successfully.");

        // انتظار 20 ثانية قبل تنفيذ المهمة الثانية
        setTimeout(async () => {
            try {
                await instance.post("price/run-price");
                toast.success("Task 2 executed successfully.");

                // انتظار 15 ثانية قبل تنفيذ المهمة الثالثة
                setTimeout(async () => {
                    try {
                        await instance.post("price/run-price3");
                        toast.success("Task 3 executed successfully.");

                        // انتظار 10 ثوانٍ قبل تنفيذ المهمة الرابعة
                        setTimeout(async () => {
                            try {
                                await instance.get("price/products-all");
                                toast.success("Task 4 executed successfully.");

                                // انتظار 10 ثوانٍ قبل إعادة تحميل الصفحة
                                setTimeout(() => {
                                    window.location.reload();
                                }, 10000);

                            } catch (error) {
                                console.error("Error executing Task 4:", error);
                                toast.error("Error executing Task 4: " + error.message);
                            }
                        }, 10000);

                    } catch (error) {
                        console.error("Error executing Task 3:", error);
                        toast.error("Error executing Task 3: " + error.message);
                    }
                }, 15000);
            } catch (error) {
                console.error("Error executing Task 2:", error);
                toast.error("Error executing Task 2: " + error.message);
            }
        }, 20000);
    } catch (error) {
        console.error("Error executing Task 1:", error);
        toast.error("Error executing Task 1: " + error.message);
    }
};

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
      <header>
        <h1>Price Products in City</h1>
      </header>
      <div style={{ textAlign: "center" }}>
        <button onClick={() => navigate("/")} className="button">
          ← Main
        </button>
        {userRole !== "supervisor" && (
          <>
            <button
              onClick={() => (window.location.href = "/unique")}
              className="button"
            >
              products
            </button>
            <button className="button" onClick={handleRunTaskSequence}>
              Run Task
            </button>
          </>
        )}
        <button
          onClick={() => (window.location.href = "/price")}
          className="button"
        >
          Price
        </button>
        <button
          onClick={() => (window.location.href = "/price-governorate")}
          className="button"
        >
          Price Governorate
        </button>

        {userRole !== "supervisor" && (
          <button
            onClick={() => (window.location.href = "/price-all")}
            className="button"
          >
            Price All
          </button>
        )}
        
      </div>

      <CustomerSearch
        searchTerm={searchTerm}
        excludeTerm={excludeTerm}
        datalistData={getAllUniqueValues()}
        handleSearch={handleSearch}
        handleExcludeSearch={handleExcludeSearch}
        resetSearch={resetSearch}
        dateFilter={null}
        formatDateRange={() => ""}
      />

      <table id="price-products-table">
        <thead>
          <tr>
            <th className="sortable" onClick={() => handleSort("sequence")}>
              #
            </th>
            <th className="sortable" onClick={() => handleSort("Date")}>
              Date
            </th>
            <th className="sortable" onClick={() => handleSort("City")}>
              City
            </th>
            <th className="sortable" onClick={() => handleSort("Product")}>
              Product
            </th>
                <th className="sortable" onClick={() => handleSort("Total_ProductsCount")}>
            ProductsCount
            </th>
           
            <th className="sortable" onClick={() => handleSort("Total_Weight")}>
            Weight
            </th>
            <th className="sortable" onClick={() => handleSort("Total_RepeatedMax")}>
            RMax
            </th>
            <th className="sortable" onClick={() => handleSort("Max_Price")}>
              Max Price
            </th>
            <th className="sortable" onClick={() => handleSort("Min_Price")}>
              Min Price
            </th>
            <th className="sortable" onClick={() => handleSort("Total_RepeatedMin")}>
            RMin
            </th>
          </tr>
        </thead>
        <tbody>
          {loading && page === 1 ? (
            <tr>
              <td colSpan="10">Loading...</td>
            </tr>
          ) : sortedData.length === 0 ? (
            <tr>
              <td colSpan="10">No data available</td>
            </tr>
          ) : (
            sortedData.map((item) => (
              <tr key={item.sequence}>
                <td>{item.sequence}</td>
                <td
                  style={{
                    backgroundColor: getDateCellColor(item.Date),
                  }}
                >
                  {formatDateToCustomString(item.Date)}
                </td>
                <td>{item.City}</td>
                <td>{item.Product}</td>
                <td>{item.Total_ProductsCount}</td>
                <td>{item.Total_Weight}</td>
                <td>{item.Total_RepeatedMax}</td>
                <td
                  style={{
                    backgroundColor: getPriceBackgroundColor(
                      item.Max_Price,
                      item.minMaxPrices,
                      item.Date,
                      item.Product,
                      true
                    ),
                  }}
                >
                  {item.Max_Price}
                </td>
                <td
                  style={{
                    backgroundColor: getPriceBackgroundColor(
                      item.Min_Price,
                      item.minMaxPrices,
                      item.Date,
                      item.Product,
                      false
                    ),
                  }}
                >
                  {item.Min_Price}
                </td>
                <td>{item.Total_RepeatedMin}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {hasMore && (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <button onClick={loadMore} className="button">
            Load More
          </button>
        </div>
      )}
      {loading && page > 1 && <div>Loading more...</div>}
      <footer>
        <span>© 2025 OwlTech All rights reserved.</span>
      </footer>
    </div>
  );
};

export default PriceProductsCity;
